import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Icon, Menus, Text, useTheme } from '@Sage/hoi-poi-ui';
import sanitizeHtml from 'sanitize-html';

import { getLiteral } from 'utils/getLiteral';

import './styles.scss';

const ALLOWED_TAGS = [
    'html',
    'head',
    'body',
    'meta',
    'title',
    'style',
    'center',
    'font',
    'div',
    'span',
    'p',
    'a',
    'img',
    'table',
    'caption',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'th',
    'td',
    'colgroup',
    'col',
    'ul',
    'ol',
    'li',
    'strong',
    'em',
    'b',
    'i',
    'u',
    's',
    'sub',
    'sup',
    'small',
    'code',
    'pre',
    'blockquote',
    'hr',
    'br',
    'address',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
];

const ALLOWED_ATTRIBUTES = {
    '*': [
        'class',
        'id',
        'style',
        'title',
        'align',
        'valign',
        'bgcolor',
        'border',
        'cellpadding',
        'cellspacing',
        'width',
        'height',
        'dir',
        'role',
        'aria-*',
        'data-*',
    ],
    a: ['href', 'name', 'target', 'rel', 'title', 'class', 'id', 'style'],
    body: ['bgcolor', 'background', 'text', 'link', 'vlink', 'alink', 'style', 'class', 'id'],
    col: ['span', 'width', 'style'],
    colgroup: ['span', 'width', 'style'],
    img: ['src', 'srcset', 'alt', 'title', 'width', 'height', 'style', 'border', 'align'],
    meta: ['charset', 'name', 'content', 'http-equiv'],
    table: [
        'align',
        'bgcolor',
        'background',
        'border',
        'cellpadding',
        'cellspacing',
        'width',
        'height',
        'style',
    ],
    td: [
        'align',
        'valign',
        'bgcolor',
        'background',
        'width',
        'height',
        'colspan',
        'rowspan',
        'style',
    ],
    th: [
        'align',
        'valign',
        'bgcolor',
        'background',
        'width',
        'height',
        'colspan',
        'rowspan',
        'style',
    ],
};

const ALLOWED_STYLES = {
    '*': {
        color: [/.+/],
        'background-color': [/.+/],
        background: [/.+/],
        'background-image': [/.+/],
        'background-position': [/.+/],
        'background-repeat': [/.+/],
        'background-size': [/.+/],
        'font-family': [/.+/],
        'font-size': [/.+/],
        'font-weight': [/.+/],
        'font-style': [/.+/],
        'line-height': [/.+/],
        'letter-spacing': [/.+/],
        'text-align': [/.+/],
        'text-decoration': [/.+/],
        'text-transform': [/.+/],
        'white-space': [/.+/],
        'word-break': [/.+/],
        'overflow-wrap': [/.+/],
        overflow: [/.+/],
        'overflow-x': [/.+/],
        'overflow-y': [/.+/],
        display: [/.+/],
        float: [/.+/],
        clear: [/.+/],
        position: [/.+/],
        top: [/.+/],
        right: [/.+/],
        bottom: [/.+/],
        left: [/.+/],
        width: [/.+/],
        'min-width': [/.+/],
        'max-width': [/.+/],
        height: [/.+/],
        'min-height': [/.+/],
        'max-height': [/.+/],
        margin: [/.+/],
        'margin-top': [/.+/],
        'margin-right': [/.+/],
        'margin-bottom': [/.+/],
        'margin-left': [/.+/],
        padding: [/.+/],
        'padding-top': [/.+/],
        'padding-right': [/.+/],
        'padding-bottom': [/.+/],
        'padding-left': [/.+/],
        border: [/.+/],
        'border-top': [/.+/],
        'border-right': [/.+/],
        'border-bottom': [/.+/],
        'border-left': [/.+/],
        'border-width': [/.+/],
        'border-style': [/.+/],
        'border-color': [/.+/],
        'border-collapse': [/.+/],
        'border-spacing': [/.+/],
        'border-radius': [/.+/],
        'vertical-align': [/.+/],
        'text-indent': [/.+/],
        opacity: [/.+/],
        'list-style-type': [/.+/],
        'list-style-position': [/.+/],
        direction: [/.+/],
        'table-layout': [/.+/],
        'box-sizing': [/.+/],
        'mso-line-height-rule': [/.+/],
        'mso-padding-alt': [/.+/],
    },
};

const SANITIZE_OPTIONS = {
    allowedTags: ALLOWED_TAGS,
    allowedAttributes: ALLOWED_ATTRIBUTES,
    allowedStyles: ALLOWED_STYLES,
    allowedSchemes: ['http', 'https', 'mailto', 'tel', 'data', 'cid'],
    allowedSchemesByTag: {
        a: ['http', 'https', 'mailto', 'tel'],
        img: ['http', 'https', 'data', 'cid'],
    },
    allowVulnerableTags: true,
};

const PREVIEW_BASE_STYLES = `
    html, body {
        margin: 0;
        padding: 0;
        width: 100%;
        max-width: 100%;
        overflow-x: hidden;
        scrollbar-width: none;
        -ms-overflow-style: none;
        box-sizing: border-box;
    }

    html::-webkit-scrollbar,
    body::-webkit-scrollbar {
        width: 0;
        height: 0;
        display: none;
    }

    *, *::before, *::after {
        box-sizing: inherit;
    }

    video, canvas, svg, table {
        max-width: 100%;
    }
`;

const ensureHtmlDocument = (html) => {
    if (!html?.trim()) return '';

    if (typeof DOMParser === 'undefined') {
        return sanitizeHtml(html, SANITIZE_OPTIONS);
    }

    const parser = new DOMParser();
    const documentNode = parser.parseFromString(html, 'text/html');

    documentNode.querySelectorAll('script').forEach((scriptElement) => {
        scriptElement.remove();
    });

    documentNode.querySelectorAll('a').forEach((linkElement) => {
        linkElement.setAttribute('target', '_blank');
        linkElement.setAttribute('rel', 'noopener noreferrer');
    });

    if (!documentNode.querySelector('meta[charset]')) {
        const metaCharset = documentNode.createElement('meta');
        metaCharset.setAttribute('charset', 'UTF-8');
        documentNode.head.prepend(metaCharset);
    }

    if (!documentNode.querySelector('meta[name="viewport"]')) {
        const viewportMeta = documentNode.createElement('meta');
        viewportMeta.setAttribute('name', 'viewport');
        viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0');
        documentNode.head.appendChild(viewportMeta);
    }

    const previewStyles = documentNode.createElement('style');
    previewStyles.setAttribute('data-preview-base-styles', 'true');
    previewStyles.textContent = PREVIEW_BASE_STYLES;
    documentNode.head.prepend(previewStyles);

    return sanitizeHtml(documentNode.documentElement.outerHTML, SANITIZE_OPTIONS);
};

const HtmlPreviewFrame = memo(
    ({
        actionIcon,
        actionLabel,
        className = '',
        html,
        menuItems,
        onClickAction,
        title,
        sandbox = 'allow-popups',
        scale,
        withInteraction = false,
        isPreviewOpen = false,
    }) => {
        const iframeRef = useRef(null);
        const isHoverLockedRef = useRef(false);
        const theme = useTheme();
        const [isMenuOpen, setIsMenuOpen] = useState(false);
        const [isHovered, setIsHovered] = useState(false);
        const hasAction = Boolean(onClickAction);
        const hasMenuItems = Array.isArray(menuItems) && menuItems.length > 0;
        const isHoverOverlayVisible = isHovered && !isPreviewOpen;

        const lockHover = () => {
            setIsHovered(false);
            isHoverLockedRef.current = true;
        };

        const handleHostMouseEnter = () => {
            if (!isHoverLockedRef.current) {
                setIsHovered(true);
            }
        };

        const handleHostMouseLeave = () => {
            isHoverLockedRef.current = false;
            setIsHovered(false);
        };

        const handleTriggerClick = (event) => {
            lockHover();
            onClickAction?.();
            event.currentTarget.blur();
        };

        const documentHtml = useMemo(() => ensureHtmlDocument(html), [html]);

        useEffect(() => {
            if (!iframeRef.current) return;
            iframeRef.current.srcdoc = documentHtml;
        }, [documentHtml]);

        useEffect(() => {
            if (isPreviewOpen) {
                lockHover();
            }
        }, [isPreviewOpen]);

        const iframe = (
            <iframe ref={iframeRef} title={title} className={className} sandbox={sandbox} />
        );

        const previewTriggerButton =
            hasAction && actionLabel ? (
                <button
                    type="button"
                    className="html-preview-frame__modal-trigger"
                    aria-label={actionLabel}
                    onClick={handleTriggerClick}
                >
                    <span className="html-preview-frame__modal-hint">
                        {actionIcon ? (
                            <Icon
                                name={actionIcon}
                                size="raw"
                                color={theme.colors.primary.white}
                                aria-hidden
                            />
                        ) : null}
                        <Text type="body" color={theme.colors.primary.white}>
                            {actionLabel}
                        </Text>
                    </span>
                </button>
            ) : null;

        const previewMenu = hasMenuItems ? (
            <div
                className="html-preview-frame__menu-wrap"
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
            >
                <Menus
                    items={menuItems}
                    size="medium"
                    classNameMenu="html-preview-frame__menu-popover"
                    onVisibleChange={setIsMenuOpen}
                    title={
                        <button
                            type="button"
                            className="html-preview-frame__menu-trigger"
                            aria-haspopup="true"
                            aria-label={getLiteral('label_action')}
                        >
                            <Icon name="moreVert" color={theme.colors.actionMinor[500]} />
                        </button>
                    }
                />
            </div>
        ) : null;

        const staticOverlayInside =
            !hasAction && !withInteraction ? (
                <div className="html-preview-frame__overlay" aria-hidden="true" />
            ) : null;

        const innerRelative = (
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                {iframe}
                {scale ? (
                    staticOverlayInside
                ) : (
                    <>
                        {previewTriggerButton}
                        {staticOverlayInside}
                    </>
                )}
            </div>
        );

        const scaledContent = !scale ? (
            hasMenuItems ? (
                <div
                    className={classNames('html-preview-frame__host', {
                        'html-preview-frame__host--active': isMenuOpen,
                        'html-preview-frame__host--hovered': isHoverOverlayVisible,
                    })}
                    onMouseEnter={handleHostMouseEnter}
                    onMouseLeave={handleHostMouseLeave}
                >
                    {innerRelative}
                    {previewMenu}
                </div>
            ) : (
                innerRelative
            )
        ) : (
            <div
                className={classNames('html-preview-frame--scaled-host', {
                    'html-preview-frame--scaled-host--active': isMenuOpen,
                    'html-preview-frame--scaled-host--hovered': isHoverOverlayVisible,
                })}
                onMouseEnter={handleHostMouseEnter}
                onMouseLeave={handleHostMouseLeave}
            >
                <div className="html-preview-frame--scaled" style={{ '--scale': scale }}>
                    {innerRelative}
                </div>
                {previewTriggerButton}
                {previewMenu}
            </div>
        );

        return scaledContent;
    },
);

HtmlPreviewFrame.displayName = 'HtmlPreviewFrame';

HtmlPreviewFrame.propTypes = {
    actionIcon: PropTypes.string,
    actionLabel: PropTypes.string,
    className: PropTypes.string,
    html: PropTypes.string,
    isPreviewOpen: PropTypes.bool,
    menuItems: PropTypes.arrayOf(PropTypes.object),
    onClickAction: PropTypes.func,
    sandbox: PropTypes.string,
    scale: PropTypes.number,
    title: PropTypes.string,
    withInteraction: PropTypes.bool,
};

export default HtmlPreviewFrame;
